
export const method = {
  hero: {
    label: 'Processo Técnico',
    title: 'Método FGT',
    subtitle: 'Estratégia sem execução é teoria. Execução sem método é risco.',
    description: 'Processo técnico de 5 etapas para estruturação patrimonial via consórcios regulamentados — da análise ao acompanhamento.',
    stats: [
      { value: '5', label: 'Etapas estruturadas' },
      { value: '100%', label: 'Baseado em regulamento' },
      { value: '1 dia útil', label: 'Retorno do diagnóstico' }
    ]
  },
  philosophy: {
    label: 'Fundamentos',
    title: 'Uma abordagem técnica, não comercial',
    paragraph1: 'O Método FGT foi desenvolvido para eliminar a subjetividade das decisões patrimoniais. Cada etapa é documentada, cada variável é mapeada e cada cenário é modelado antes de qualquer recomendação.',
    paragraph2: 'Não trabalhamos com intuição ou promessas. Trabalhamos com análise técnica, regulamentos e dados reais de grupos ativos.',
    pillars: [
      {
        icon: 'ri-search-eye-line',
        title: 'Diagnóstico antes da solução',
        text: 'Nenhuma estrutura é proposta sem mapeamento completo da situação patrimonial, objetivos e restrições do cliente.'
      },
      {
        icon: 'ri-bar-chart-grouped-line',
        title: 'Modelagem de cenários',
        text: 'Construímos cenários base e stress para cada estrutura, com projeção de custos, prazos e riscos operacionais.'
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Conformidade regulatória',
        text: 'Toda análise é baseada no regulamento vigente da administradora e nas normas do Banco Central do Brasil.'
      },
      {
        icon: 'ri-route-line',
        title: 'Acompanhamento contínuo',
        text: 'O processo não termina na adesão. Monitoramos assembleias, oportunidades de lance e suportamos até a utilização do crédito.'
      }
    ]
  },
  steps: [
    {
      number: '01',
      title: 'Diagnóstico Patrimonial',
      description: 'Mapeamento completo da situação atual: objetivos, capacidade de pagamento, horizonte temporal, perfil de risco e restrições operacionais.',
      details: [
        'Análise de fluxo de caixa pessoal ou corporativo',
        'Identificação de objetivos patrimoniais prioritários',
        'Avaliação de capacidade de parcela mensal',
        'Definição de horizonte de planejamento',
        'Levantamento de restrições documentais e operacionais'
      ],
      duration: '1 reunião de 60–90 min',
      deliverable: 'Relatório de diagnóstico patrimonial com mapeamento de objetivos e capacidade'
    },
    {
      number: '02',
      title: 'Análise de Grupo e Regulamento',
      description: 'Estudo técnico de grupos ativos, administradoras, taxas, fundos de reserva, dinâmica de contemplação e regras operacionais.',
      details: [
        'Comparação de administradoras e regulamentos vigentes',
        'Análise de taxas de administração e fundos de reserva',
        'Estudo de histórico e dinâmica de contemplação',
        'Validação de restrições e exigências documentais',
        'Avaliação de solidez e reputação da administradora'
      ],
      duration: '2–3 dias úteis',
      deliverable: 'Análise comparativa de grupos com recomendação técnica fundamentada'
    },
    {
      number: '03',
      title: 'Modelagem de Cenários',
      description: 'Construção de cenários base e stress: projeção de prazos, custos totais, impacto de lances, análise de risco e retorno esperado.',
      details: [
        'Cenário base: contemplação por sorteio sem lance',
        'Cenário stress: contemplação por lance com diferentes percentuais',
        'Projeção de custos totais (taxas + fundos + correção)',
        'Análise de sensibilidade e risco operacional',
        'Comparativo com alternativas de crédito convencionais'
      ],
      duration: '2–4 dias úteis',
      deliverable: 'Documento de modelagem com 3 cenários, projeções e análise de risco'
    },
    {
      number: '04',
      title: 'Estrutura de Execução',
      description: 'Definição da estratégia de entrada, escolha de grupo, estruturação de lance (quando aplicável), preparação documental e formalização.',
      details: [
        'Seleção definitiva de grupo e administradora',
        'Estruturação de estratégia de contemplação por lance',
        'Checklist e preparação de documentação necessária',
        'Formalização e adesão ao grupo selecionado',
        'Briefing completo sobre obrigações e direitos do consorciado'
      ],
      duration: '3–7 dias úteis',
      deliverable: 'Contrato de adesão formalizado e guia de obrigações do consorciado'
    },
    {
      number: '05',
      title: 'Acompanhamento até Utilização',
      description: 'Gestão contínua: monitoramento de assembleias, análise de oportunidades de lance, suporte na contemplação e utilização do crédito.',
      details: [
        'Monitoramento mensal de assembleias e resultados',
        'Análise de oportunidades de lance em cada assembleia',
        'Suporte completo na contemplação e documentação',
        'Gestão da liberação e utilização do crédito',
        'Acompanhamento até encerramento total da operação'
      ],
      duration: 'Durante toda a vigência do grupo',
      deliverable: 'Relatórios mensais de acompanhamento e suporte dedicado até utilização'
    }
  ],
  stepsSection: {
    label: 'As 5 Etapas',
    title: 'Do diagnóstico à utilização do crédito',
    subtitle: 'Cada etapa tem entregáveis definidos, prazo estimado e responsabilidades claras.',
    durationLabel: 'Prazo estimado',
    deliverableLabel: 'Entregável',
    activitiesLabel: 'Atividades desta etapa'
  },
  differentials: {
    label: 'Diferenciais',
    title: 'Por que o método importa',
    subtitle: 'A diferença entre uma boa decisão e uma decisão cara está na qualidade da análise que a precede.',
    items: [
      {
        icon: 'ri-microscope-line',
        title: 'Análise técnica independente',
        text: 'Não somos representantes de administradoras. Nossa análise é independente e baseada exclusivamente no interesse do cliente.'
      },
      {
        icon: 'ri-file-chart-line',
        title: 'Documentação de cada etapa',
        text: 'Cada fase gera um entregável documentado: diagnóstico, análise comparativa, modelagem de cenários e relatórios de acompanhamento.'
      },
      {
        icon: 'ri-scales-3-line',
        title: 'Conformidade com o BACEN',
        text: 'Todas as estruturas respeitam as normas do Banco Central do Brasil e os regulamentos vigentes das administradoras autorizadas.'
      },
      {
        icon: 'ri-focus-3-line',
        title: 'Foco no objetivo, não no produto',
        text: 'Partimos do objetivo patrimonial do cliente para selecionar a estrutura mais adequada — não o contrário.'
      },
      {
        icon: 'ri-team-line',
        title: 'Suporte dedicado e contínuo',
        text: 'Um consultor dedicado acompanha cada operação do início ao fim, com disponibilidade para dúvidas e decisões ao longo do processo.'
      },
      {
        icon: 'ri-eye-line',
        title: 'Transparência total de custos',
        text: 'Todos os custos são mapeados antes da adesão: taxa de administração, fundo de reserva, seguro e eventuais encargos por inadimplência.'
      }
    ]
  },
  governance: {
    label: 'Governança',
    title: 'Transparência como princípio',
    paragraph1: 'O Método FGT opera sob princípios rígidos de transparência e conformidade regulatória. Cada recomendação é fundamentada em dados verificáveis e regulamentos públicos.',
    disclaimer: 'O método FGT não garante contemplação em prazo específico. Não promete retorno fixo ou rentabilidade determinada. Todas as projeções dependem do regulamento da administradora, dinâmica do grupo e decisões de assembleia.',
    items: [
      {
        icon: 'ri-bank-line',
        title: 'Regulado pelo Banco Central',
        text: 'Consórcios são regulamentados pelo BACEN (Lei 11.795/2008). Operamos exclusivamente com administradoras autorizadas.'
      },
      {
        icon: 'ri-lock-line',
        title: 'Sem promessa de contemplação',
        text: 'Nenhum consultor FGT promete prazo de contemplação. Modelamos cenários — não garantimos resultados.'
      },
      {
        icon: 'ri-file-shield-2-line',
        title: 'Documentação completa',
        text: 'Toda operação é documentada: diagnóstico, análise, modelagem, contrato e relatórios de acompanhamento.'
      }
    ]
  },
  cta: {
    title: 'Aplique o método ao seu caso',
    subtitle: 'Agendar um diagnóstico estratégico personalizado. Gratuito e sem compromisso.',
    button: 'Agendar Diagnóstico Estratégico',
    secondary: 'Falar pelo WhatsApp'
  }
};
